import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import QTIAssessmentItem from './QTIAssessmentItem';
import QTIAssessmentItemWithDrawing from './QTIAssessmentItemWithDrawing';

import assessmentActions from '../../actions/AssessmentActions';
import visitedQuestionsActions from '../../actions/VisitedQuestionsActions';

import durationTrackingService from '../../services/DurationTrackingService';
import LoadTracker from '../../services/LoadTracker';
import responseService from '../../services/ResponseService';
import { t } from '../../services/i18nService';
import questionHelperServices from '../../services/questionView/QuestionHelperServices';
import scrollingService from '../../services/ScrollingService';
import stimulusHighlightsService from '../../services/StimulusHighlightsService';
import { withIcewormFocusTrap } from '../focus/withFocusTrap';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import storeManager from '../../store/StoreManager';

import ScantronSketches from './ScantronSketches';
import QuestionDisplayFooter from './QuestionDisplayFooter';
import QuestionDisplayFlag from './itemFlag/QuestionDisplayFlag';
import QuestionNumber from './QuestionNumber';
import QuestionManualSelection from './QuestionManualSelection';
import QuestionPDFPanelDisplay from './QuestionPDFPanelDisplay';
import QuestionRubricBlockDisplay from './QuestionRubricBlockDisplay';
import QuestionKeyWordListDisplay from './QuestionKeyWordList';
import FeedbackDisplay from './feedback/FeedbackDisplay';
import ModalFeedbackDisplay from './feedback/ModalFeedbackDisplay';

import styles from './QuestionDisplay.scss';
import './paperBasedStyles/ceqPBStyles.scss';
import constants from '../../qti/Constants';
import SectionRubricBlock from '../../themes/components/fjord/SectionRubricBlock';
import questionDisplaySelector from '../../selectors/question/QuestionDisplaySelector';
import QuestionRubricBlockWithTitle from './QuestionRubricBlockWithTitle';
import reloginService from '../../services/ReloginService';
import stateCacheService from '../../services/StateCache';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import questionSelectionService from '../../services/QuestionSelectionService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration, getA11yAttributesV2 } from '../../services/a11yIIBScreenReaderService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import uiService from '../../services/UIService';
import questionUtils from '../../qti/utils/questionUtils';

export class Question extends React.PureComponent {
    static propTypes = {
        assessmentRunId: PropTypes.number,
        activateAndScrollToQuestion: PropTypes.func,
        additionalQuestionClassList: PropTypes.array.isRequired,
        additionalWrapperClassList: PropTypes.array.isRequired,
        annotatableQuestionHandler: PropTypes.func,
        choiceSequences: PropTypes.object.isRequired,
        context: PropTypes.string.isRequired,
        correctResponses: PropTypes.object,
        currentScorableItem: PropTypes.number,
        extendedRenderingActivated: PropTypes.bool.isRequired,
        feedback: PropTypes.object,
        flaggedItems: PropTypes.array,
        hasHighlight: PropTypes.bool,
        headerId: PropTypes.string.isRequired,
        highlightContentItemId: PropTypes.number,
        interactionSupportsRTL: PropTypes.bool.isRequired,
        isContentBlock: PropTypes.bool,
        isCurrent: PropTypes.bool,
        isDocument: PropTypes.bool,
        isForm: PropTypes.bool,
        isInSectionView: PropTypes.bool.isRequired,
        isLastQuestionInCurrentNestedSection: PropTypes.bool,
        isManualSelectorReadOnly: PropTypes.bool.isRequired,
        isReadOnly: PropTypes.bool,
        isReadOnlyBasedOnManualSelectionSetup: PropTypes.bool.isRequired,
        isRemoteOralExam: PropTypes.bool.isRequired,
        isSplitQuestionView: PropTypes.bool.isRequired,
        isViewMediaScreen: PropTypes.bool.isRequired,
        isVisited: PropTypes.bool.isRequired,
        isWordFormationQuestion: PropTypes.bool.isRequired,
        isA11yIterationForIIBScreenReaderEnabled: PropTypes.bool.isRequired,
        isA11yV5QuestionClickHandlerEnabled: PropTypes.bool,
        isScorableItemRoute: PropTypes.bool,
        mainQuestionWrapperLabel: PropTypes.string,
        mode: PropTypes.string,
        onReady: PropTypes.func.isRequired,
        onContentLayoutReady: PropTypes.func,
        outcomeVariables: PropTypes.object,
        presignedUrlsForPDFPanel: PropTypes.object,
        question: PropTypes.object.isRequired,
        questionInteractionVisibility: PropTypes.object,
        questionNumberId: PropTypes.string.isRequired,
        questionSelection: PropTypes.object,
        section: PropTypes.object,
        sectionHasStimulus: PropTypes.bool,
        sectionId: PropTypes.number,
        sectionQuestionsCount: PropTypes.number,
        settingValues: PropTypes.object,
        shouldScrollToFirstInteraction: PropTypes.bool.isRequired,
        shouldShowInteractionFeedback: PropTypes.bool.isRequired,
        shouldShowManualSelector: PropTypes.bool.isRequired,
        shouldShowModalFeedback: PropTypes.bool.isRequired,
        shouldShowPdfPanel: PropTypes.bool.isRequired,
        shouldShowScantronSketches: PropTypes.bool.isRequired,
        shouldShowTitle: PropTypes.bool.isRequired,
        showFlagging: PropTypes.bool.isRequired,
        showKeyWordListBlock: PropTypes.bool.isRequired,
        showQuestionRubricBlock: PropTypes.bool.isRequired,
        stimulusPosition: PropTypes.string,
        themeInstance: PropTypes.object.isRequired,
        themeQuestionLabel: PropTypes.object,
        ui: PropTypes.object.isRequired,
        useNumberPerInteraction: PropTypes.bool.isRequired,
        userResponses: PropTypes.object.isRequired,
        viewMedia: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = {
            isTabPressed: false,
            canvasOpened: false,
            showQuestion: false,
        };
        this.isCSCB = questionSelectionService.isCandidateSelectedContentBlockEnabled();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.initLoadTracker(this.props);
    }

    componentDidMount() {
        const { assessmentRunId, question, isInSectionView, hasHighlight, mode, highlightContentItemId, isVisited,
            shouldScrollToFirstInteraction, themeInstance, isScorableItemRoute } = this.props;

        if (assessmentRunId && question?.contentRevisionId && reloginService.shouldStoreNextQuestion()) {
            stateCacheService.getStateCacheInstance().setLastVisitedQuestion(assessmentRunId, question.contentRevisionId);
        }

        if (!isVisited) {
            this.setAsVisited(question.contentRevisionId);
        }
        this.renderedTracker(true);

        if (shouldScrollToFirstInteraction) {
            scrollingService.scrollToFirstInteraction();
        }

        if (!isInSectionView) {
            if (!this.isCSCB && !isScorableItemRoute) {
                scrollingService.scrollToTop();
            }
            this.focus();
            stimulusHighlightsService.goToHighlight(question, hasHighlight, mode, highlightContentItemId);
        }

        durationTrackingService.setNewDurationReferenceTimestamp(question.contentRevisionId);
        themeInstance.performPostComponentMountActions(`#question-${question.contentRevisionId}`);

        document.addEventListener('keydown', this.handleKeyPress);
        document.addEventListener('click', this.resetKeyboardPress);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (nextProps.question !== this.props.question) {
            this.initLoadTracker(nextProps);
            responseService.updateItemResultOnNavigation(this.props.question.contentRevisionId);
            durationTrackingService.setNewDurationReferenceTimestamp(nextProps.question.contentRevisionId);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            isInSectionView,
            question,
            hasHighlight,
            mode,
            highlightContentItemId,
            isVisited,
            isWordFormationQuestion,
            isScorableItemRoute,
            themeInstance,
        } = this.props;

        const shouldSetFocusToQuestion = !this.state.isTabPressed &&
            (prevProps.question !== question ||
            this.props.currentScorableItem !== prevProps.currentScorableItem);

        if (isWordFormationQuestion && this.props.currentScorableItem !== prevProps.currentScorableItem) {
            this.scrollToScorableInput();
        }

        if (shouldSetFocusToQuestion) this.focusActiveQuestion();

        if (prevProps.question === question) {
            return;
        }

        this.renderedTracker(true);
        if (!isVisited) {
            this.setAsVisited(question.contentRevisionId);
        }

        if (!isInSectionView) {
            if (!this.isCSCB && !isScorableItemRoute) {
                scrollingService.scrollToTop();
            }
            this.focus();
            stimulusHighlightsService.goToHighlight(question, hasHighlight, mode, highlightContentItemId);
        }

        themeInstance.performPostComponentDidUpdate(`#question-${question.contentRevisionId}`);

        if (isWordFormationQuestion && prevProps.question?.contentRevisionId !== this.props.question?.contentRevisionId) {
            this.props.onContentLayoutReady();
        }
    }

    focusActiveQuestion = () => {
        // Trigger focus on interaction container when navigation is finished
        setTimeout(() => {
            const activeInteractionElement = document.querySelector('div[role="main"] .active');
            if (activeInteractionElement) {
                const parentInteractionContainer = activeInteractionElement.closest('.interaction-container');
                const textInputField = parentInteractionContainer.querySelector('input[type="text"]');
                if (textInputField) {
                    textInputField.focus();
                    return;
                }

                parentInteractionContainer.focus();

                parentInteractionContainer.addEventListener('blur', () => {
                    parentInteractionContainer.removeAttribute('tabindex');
                });
            }
        });
    }

    handleKeyPress = (event) => {
        this.setState({ isTabPressed: event.code === 'Tab' });
    }

    resetKeyboardPress = () => {
        this.setState({ isTabPressed: false });
    }

    scrollToScorableInput = () => {
        const { currentScorableItem, onContentLayoutReady } = this.props;
        const selectedScorableItem = document.querySelectorAll(`[placeholder="${currentScorableItem}"]`);
        const selectedInput = selectedScorableItem.item(0);
        if (selectedInput) {
            selectedInput.scrollIntoView({ block: 'center', inline: 'center' });
        }

        // scrollIntoView calls must be async to each other because question wrapper and keywords wrapper may or may not have the same scrollbar
        setTimeout(() => {
            const selectedKeyWord = document.getElementById(`keyword-${currentScorableItem}`);
            if (selectedKeyWord) {
                selectedKeyWord.scrollIntoView({ block: 'nearest', inline: 'center' });
            }

            if (onContentLayoutReady) {
                onContentLayoutReady();
            }
        }, 100);
    }

    componentWillUnmount() {
        const { isInSectionView, isRemoteOralExam, question: { contentRevisionId } } = this.props;
        // remote oral exam handles the question tracking by itself
        if (!isInSectionView || !isRemoteOralExam) {
            responseService.updateItemResultOnNavigation(contentRevisionId);
        }

        document.removeEventListener('keydown', this.handleKeyPress);
        document.removeEventListener('click', this.resetKeyboardPress);
    }

    handleCanvasStateChange = (canvasState) => {
        this.setState({
            canvasOpened: canvasState.canvasOpened,
            showQuestion: canvasState.showQuestion,
        });
    };

    setAsVisited(contentRevisionId) {
        storeManager.getStore().dispatch(
            visitedQuestionsActions.setVisited(contentRevisionId)
        );
    }

    activateQuestionAndHighlight = () => {
        const { question, activateAndScrollToQuestion, isCurrent, isInSectionView } = this.props;
        if (!isInSectionView || isCurrent || !activateAndScrollToQuestion) {
            return false;
        }

        return setTimeout(() => {
            activateAndScrollToQuestion(question.contentRevisionId, 10, false, true);
        }, 100);
    };

    activateQuestionOnPrimaryMouseDown = (event) => {
        if (event.button === 0) {
            this.activateQuestionAndHighlight();
        }
    };

    handleTouchStart = (event) => {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
    };

    handleTouchEnd = (event) => {
        const TAP_THRESHOLD = 10;
        const touch = event.changedTouches[0];
        const dx = Math.abs(touch.clientX - this.touchStartX);
        const dy = Math.abs(touch.clientY - this.touchStartY);

        if (dx < TAP_THRESHOLD && dy < TAP_THRESHOLD) {
            const activationResult = this.activateQuestionAndHighlight();

            // Only prevent the default touch behavior (and thus the synthetic mouse event)
            // when we are actually going to activate/scroll the question. This avoids
            // suppressing click/focus on interactive controls when activation is a no-op.
            if (activationResult !== false) {
                event.preventDefault();
            }
        }
    };


    hasDrawingInteraction = (question) => {
        return question?.responseModels?.some(model => model.type === constants.interactionType.DRAWING_INTERACTION);
    };

    hasMandatoryQuestion = (question) => {
        return marketplacePropertiesService.isMandatoryQuestionEnabled() && question.isMandatoryQuestion;
    }

    focus() {
        const { isViewMediaScreen, isA11yIterationForIIBScreenReaderEnabled, shouldShowTitle } = this.props;
        if (!isViewMediaScreen) {
            return;
        }

        if (isA11yIterationForIIBScreenReaderEnabled) {
            const candidateInfoElement = document.getElementById('candidate-info');
            if (candidateInfoElement && document.activeElement !== candidateInfoElement) {
                candidateInfoElement.focus();
            }
        } else if (shouldShowTitle && this.h2) {
            this.h2.focus();
        } else if (this.questionNumberInstance) {
            this.questionNumberInstance.focus();
        }
    }

    initLoadTracker(props) {
        const { onReady, shouldShowPdfPanel, shouldShowScantronSketches, question } = props;

        this.loadTracker = new LoadTracker((success) => {
            if (typeof onReady === 'function') {
                onReady(success, this);
            }
        }, `QuestionDisplay - ${question.identifier}`);

        this.questionTracker = this.loadTracker.addItemToTrack('AssessmentItem');
        this.renderedTracker = this.loadTracker.addItemToTrack('QuestionDisplay');
        if (question.rubricBlockModel) {
            this.rubricBlockTracker = this.loadTracker.addItemToTrack('RubricBlock');
        }

        if (shouldShowPdfPanel) {
            this.pdfPanelTracker = this.loadTracker.addItemToTrack('PdfPanel');
        }

        if (shouldShowScantronSketches) {
            this.sketchesTracker = this.loadTracker.addItemToTrack('ScantronSketches');
        }

        this.loadTracker.setAllItemsAdded();
    }

    handleUserResponse = (...args) => {
        responseService.handleUserResponse(...args);
    };

    /**
     * Creates the aria-labelledby value referencing actual DOM elements
     * @returns {string} Space-separated list of IDs for aria-labelledby
     */
    getAriaLabelledByForA11y = () => {
        const { question, questionNumberId, headerId, shouldShowTitle } = this.props;
        const idList = [];

        if (question.number) {
            idList.push(questionNumberId);
        }
        if (shouldShowTitle && headerId) {
            idList.push(headerId);
        }
        const questionContentId = `interactionWrapper_${question.contentRevisionId}`;
        idList.push(questionContentId);

        return idList.join(' ');
    };

    renderHeaderWithMandatoryBadge(showTitle, headerId, question, headerPrefix) {
        const mandatoryBadge = this.hasMandatoryQuestion(question) ? (
            <span key="mandatoryBadge" className={styles.mandatoryBadgeWrapper}>
                <span className={styles.mandatoryBadge} aria-label="Mandatory question">
                    {t('mandatory.question.label')}
                </span>
                <span className={styles.mandatoryTooltip}>
                    <span dangerouslySetInnerHTML={{ __html: t('mandatory.question.title.bubble') }} />
                </span>
            </span>
        ) : null;

        const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v5
        );
        const tabIndex = isA11yV5Enabled ? undefined : '0';
        const a11yAttributesV2 = getA11yAttributesV2({
            'tabIndex': tabIndex,
            'aria-labelledby': this.getAriaLabelledByForA11y(),
            'role': 'region',
        });

        const ariaHiddenValueForA11y = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v2
        ) ? 'true' : undefined;

        const isA11yV4Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v4
        );

        const titleNode = showTitle ? (
            <div key="title" className={styles.questionHeaderRow} {...a11yAttributesV2}>
                <h2 tabIndex="-1" aria-hidden={ariaHiddenValueForA11y} ref={(h2) => { this.h2 = h2; }}>
                    {headerPrefix}{isA11yV4Enabled && ' '}<span id={headerId}>{question.title}</span>
                </h2>
            </div>
        ) : null;

        const items = [];
        if (!titleNode) {
            items.push(mandatoryBadge);
        } else {
            if (titleNode) items.push(titleNode);
            items.push(mandatoryBadge);
        }

        return (
            <div className={styles.questionHeaderSpacer}>
                {items}
            </div>
        );
    }

    shouldShowFooter() {
        const { isSplitQuestionView, question } = this.props;
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(question);

        if (settingsReader.shouldUsePaperBasedForMCQOrientation(question)) {
            return false;
        }

        if (isMultipleInteractionsGapMatchQuestion) {
            return !isSplitQuestionView && isPaperBased;
        }

        return !isSplitQuestionView;
    }

    /**
     * Renders the question content shared between legacy and v5 landmarks.
     */
    renderQuestionContentBlock(renderParams) {
        const {
            canvasOpened, showTitle, headerPrefix, getReadOnlyStatusForAssessmentItem,
            mainQuestionWrapperClassList, getMainQuestionWrapperLabel, showWordFormationPbLayout,
            services, showFooter, zoomFactor,
        } = renderParams;

        const {
            choiceSequences, context, correctResponses, currentScorableItem, flaggedItems, headerId,
            interactionSupportsRTL, isContentBlock, isCurrent, isDocument, isForm, isManualSelectorReadOnly,
            isLastQuestionInCurrentNestedSection = false, isReadOnly, mode, outcomeVariables, question,
            questionInteractionVisibility, section, sectionHasStimulus, sectionQuestionsCount, settingValues,
            shouldShowInteractionFeedback, shouldShowManualSelector, shouldShowModalFeedback,
            shouldShowScantronSketches, shouldShowTitle, showFlagging, showQuestionRubricBlock,
            themeQuestionLabel, ui, useNumberPerInteraction, userResponses, viewMedia,
        } = this.props;

        return (
            <React.Fragment>
                { themeQuestionLabel }

                <div className={styles.questionBody}>
                    { !useNumberPerInteraction && !canvasOpened &&
                    <QuestionNumber
                        isContentBlock={isContentBlock}
                        isDocument={isDocument}
                        isForm={isForm}
                        mode={mode}
                        noTitle={!shouldShowTitle}
                        question={question}
                        ref={(instance) => { this.questionNumberInstance = instance; }}
                        sectionHasStimulus={sectionHasStimulus}
                        sectionQuestionsCount={sectionQuestionsCount}
                    />
                    }

                    <div
                        className={mainQuestionWrapperClassList.join(' ')}
                        tabIndex="-1"
                        role="group"
                        aria-labelledby={getMainQuestionWrapperLabel}
                        ref={(el) => { this.mainQuestionWrapper = el; }}
                    >
                        {this.renderHeaderWithMandatoryBadge(showTitle, headerId, question, headerPrefix)}

                        { showQuestionRubricBlock && !this.isCSCB &&
                        <QuestionRubricBlockWithTitle question={question} section={section} canvasOpened={canvasOpened} />
                        }
                        {settingsReader.shouldEnableDynamicLabel(question) && (
                            <span
                                className="paper-based-extract-label"
                                role="note"
                                aria-label={
                                    `${t('paperBased.extractLabel')
                                    } ${
                                    questionUtils?.getExtractNumber(question, mode)}`
                                }
                            >
                                {`${t('paperBased.extractLabel')
                                } ${
                                questionUtils?.getExtractNumber(question, mode)}`}
                            </span>
                        )}

                        { !isReadOnly && shouldShowManualSelector && !this.isCSCB && (
                            <QuestionManualSelection
                                contentRevisionId={question.contentRevisionId}
                                isReadOnly={isReadOnly || isManualSelectorReadOnly}
                                key={`qps-${question.contentRevisionId}`}
                            />
                        )
                        }

                        {(() => {
                            const qtiContent = this.hasDrawingInteraction(question) ? (
                                <QTIAssessmentItemWithDrawing
                                    callback={this.handleUserResponse}
                                    choiceSequences={choiceSequences}
                                    context={context}
                                    correctResponses={correctResponses}
                                    currentScorableItem={currentScorableItem}
                                    direction={interactionSupportsRTL ? ui.direction : 'ltr'}
                                    flaggedItems={flaggedItems}
                                    isCurrent={isCurrent}
                                    isReadOnly={getReadOnlyStatusForAssessmentItem}
                                    key={question.contentRevisionId}
                                    outcomeVariables={outcomeVariables}
                                    question={question}
                                    questionInteractionVisibility={questionInteractionVisibility}
                                    reportReady={this.questionTracker}
                                    services={services}
                                    settingValues={settingValues}
                                    userResponses={userResponses}
                                    viewMedia={viewMedia}
                                    feedback={this.props.feedback}
                                    shouldShowInteractionFeedback={shouldShowInteractionFeedback}
                                    zoomFactor={zoomFactor}
                                    onCanvasStateChange={this.handleCanvasStateChange}
                                />
                            ) : (
                                <QTIAssessmentItem
                                    callback={this.handleUserResponse}
                                    choiceSequences={choiceSequences}
                                    context={context}
                                    correctResponses={correctResponses}
                                    currentScorableItem={currentScorableItem}
                                    direction={interactionSupportsRTL ? ui.direction : 'ltr'}
                                    flaggedItems={flaggedItems}
                                    isCurrent={isCurrent}
                                    isReadOnly={getReadOnlyStatusForAssessmentItem}
                                    key={question.contentRevisionId}
                                    outcomeVariables={outcomeVariables}
                                    question={question}
                                    questionInteractionVisibility={questionInteractionVisibility}
                                    reportReady={this.questionTracker}
                                    services={services}
                                    settingValues={settingValues}
                                    userResponses={userResponses}
                                    viewMedia={viewMedia}
                                    feedback={this.props.feedback}
                                    shouldShowInteractionFeedback={shouldShowInteractionFeedback}
                                    zoomFactor={zoomFactor}
                                    useMCDiscreteWithOneImageMaxWidth={settingsReader.shouldUseMCDiscreteWithOneImageLayout(question)}
                                />
                            );

                            if (showWordFormationPbLayout) {
                                return (
                                    <div className={`${styles.wordFormationPbLayout} word-formation-pb-wrapper`}>
                                        <div className={styles.wordFormationPbText}>
                                            {qtiContent}
                                        </div>
                                        <QuestionKeyWordListDisplay
                                            question={question}
                                            currentScorableItem={currentScorableItem}
                                            sectionId={this.props.sectionId}
                                        />
                                    </div>
                                );
                            }

                            return qtiContent;
                        })()}

                        {
                            showFooter && (
                                <QuestionDisplayFooter
                                    isLastQuestionInCurrentNestedSection={isLastQuestionInCurrentNestedSection}
                                    question={question}
                                />
                            )
                        }
                    </div>

                    <QuestionDisplayFlag
                        currentScorableItem={currentScorableItem}
                        noTitle={!shouldShowTitle}
                        question={question}
                        showFlagging={showFlagging}
                        viewMedia={viewMedia}
                    />
                </div>

                { shouldShowScantronSketches &&
                <ScantronSketches
                    {...this.props}
                    onReady={this.sketchesTracker}
                />
                }

                { shouldShowModalFeedback &&
                <ModalFeedbackDisplay
                    {...this.props}
                    modalFeedback={this.props.feedback[constants.modalFeedbackKey]}
                />
                }

                { shouldShowInteractionFeedback &&
                <FeedbackDisplay
                    {...this.props}
                    choiceSequences={choiceSequences}
                    userResponses={userResponses}
                    services={services}
                />
                }
            </React.Fragment>
        );
    }

    /**
     * Legacy rendering: onClick directly on the role="main" div, no inner wrapper.
     */
    renderMainQuestionLandmark(renderParams) {
        const { questionClassList } = renderParams;
        const { question, ui, useNumberPerInteraction } = this.props;

        return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
            <div
                id={`question-${question.contentRevisionId}`}
                role="main"
                className={questionClassList.join(' ')}
                dir={ui.direction}
                onClick={!useNumberPerInteraction ? this.activateQuestionAndHighlight : undefined}
            >
                {this.renderQuestionContentBlock(renderParams)}
            </div>
        );
    }

    /**
     * a11y V5 rendering: no onClick on the role="main" div, content wrapped in an inner div.
     * Uses onMouseDown (primary button only) for mouse and onTouchStart/onTouchEnd with
     * tap detection for touch, preventing screen readers from announcing "clickable" on the
     * main landmark while avoiding activation during scroll gestures on touch devices.
     */
    renderMainQuestionLandmarkV5(renderParams) {
        const { questionClassList } = renderParams;
        const { question, ui, useNumberPerInteraction } = this.props;

        return (
            <div
                id={`question-${question.contentRevisionId}`}
                role="main"
                className={questionClassList.join(' ')}
                dir={ui.direction}
            >
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                    onMouseDown={!useNumberPerInteraction ? this.activateQuestionOnPrimaryMouseDown : undefined}
                    onTouchStart={!useNumberPerInteraction ? this.handleTouchStart : undefined}
                    onTouchEnd={!useNumberPerInteraction ? this.handleTouchEnd : undefined}
                >
                    {this.renderQuestionContentBlock(renderParams)}
                </div>
            </div>
        );
    }

    render() {
        const {
            additionalQuestionClassList,
            additionalWrapperClassList,
            annotatableQuestionHandler,
            extendedRenderingActivated,
            isDocument,
            isForm,
            isReadOnly,
            isReadOnlyBasedOnManualSelectionSetup,
            isSplitQuestionView,
            isWordFormationQuestion,
            mainQuestionWrapperLabel,
            presignedUrlsForPDFPanel,
            question,
            questionNumberId,
            section,
            shouldShowPdfPanel,
            shouldShowTitle,
            showFlagging,
            showKeyWordListBlock,
            stimulusPosition,
            themeInstance,
            ui,
            isA11yV5QuestionClickHandlerEnabled,
        } = this.props;
        const { canvasOpened, showQuestion } = this.state;
        const showTitle = (canvasOpened && !showQuestion) ? false : shouldShowTitle;

        const headerPrefix = (isDocument || isForm)
            ? null
            : <span className="sr-only" id={questionNumberId}>{t('generic.questionNumberPrefix')} {question.number}</span>;

        const getReadOnlyStatusForAssessmentItem = isReadOnly
        || (isReadOnlyBasedOnManualSelectionSetup && !this.isCSCB);

        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(question);
        const isInlineChoice = questionReader.isInlineChoice(question);
        const wrapperClassList = [styles.questionDisplayWrapper, ...additionalWrapperClassList];

        const classNameBasedOnMetadata = questionReader.getClassNameBasedOnMetadata(question);
        if (classNameBasedOnMetadata.length) {
            wrapperClassList.push(...classNameBasedOnMetadata);
        }
        const hasWordFormationMetadata = settingsReader.shouldUsePaperBasedWordFormation(question);
        const showWordFormationPbLayout = hasWordFormationMetadata && isWordFormationQuestion && showKeyWordListBlock;

        // CE-1374: Add single-interaction marker class for MC discrete with one image layout
        // This class is used by CSS to apply the flex layout only for single-interaction questions
        if (settingsReader.shouldUseMCDiscreteWithOneImageLayout(question)) {
            wrapperClassList.push('mc-discrete-one-image-single-interaction');
        }

        // Paper-based MCQ with image layout: positions question number beside image
        // Applies when: paper-based + CEQ MPP + MCQ with image (without specific metadata)
        if (settingsReader.shouldUsePaperBasedMCQWithImageLayout(question)) {
            wrapperClassList.push('pb-mcq-with-image-layout');
        }

        const questionClassList = [styles.question, ...additionalQuestionClassList];
        const services = questionHelperServices.getServices();
        const mainQuestionWrapperClassList = [styles.mainQuestionWrapper];
        const breakAfterSection = assessmentReader.shouldAddPageBreakOnSection(question.contentRevisionId, this.props.sectionId, section);

        if (settingsReader.shouldUsePaperBasedForMCQOrientation(question)) {
            const doesMCQHaveImages = question?.mediaModels?.some(media => media?.type === constants.mediaType.IMAGE);
            if (doesMCQHaveImages) {
                wrapperClassList.push('no-left-margin-multiple-choice-questions-pb');
            }
            if (!settingsReader.isShowMaxMarksEnabled()) {
                wrapperClassList.push('margin-between-multiple-choice-questions-pb');
            }
        }

        if (breakAfterSection) {
            wrapperClassList.push('section-page-break-after');
        }

        if (extendedRenderingActivated) {
            questionClassList.push(styles.extendedQuestionDisplay);
        }

        if (showWordFormationPbLayout) {
            questionClassList.push(styles.wordFormationPbQuestion);
            mainQuestionWrapperClassList.push(styles.extendedMainQuestionWrapperWF);
        } else if (isWordFormationQuestion) {
            questionClassList.push(styles.extendedQuestionDisplayWordFormation);
            mainQuestionWrapperClassList.push(styles.extendedMainQuestionWrapperWF);
        }

        if (showFlagging && !isMultipleInteractionsGapMatchQuestion && !isInlineChoice) {
            mainQuestionWrapperClassList.push(styles.questionContentOverflow);
        }

        if (isSplitQuestionView) {
            wrapperClassList.push('split-question-view');
        }

        if (isMultipleInteractionsGapMatchQuestion) {
            wrapperClassList.push('multiple-inline-gap-match', 'gap-match-multi-interaction-view');
        }

        themeInstance.addQuestionWrapperClassNames(question, wrapperClassList);
        themeInstance.addQuestionClassNames(question.customFields, questionClassList);
        const showFooter = this.shouldShowFooter();
        const zoomFactor = ui?.zoomFactor;

        const getMainQuestionWrapperLabel = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v2)
            ? undefined : mainQuestionWrapperLabel;

        const renderParams = {
            questionClassList,
            canvasOpened,
            showTitle,
            headerPrefix,
            getReadOnlyStatusForAssessmentItem,
            mainQuestionWrapperClassList,
            getMainQuestionWrapperLabel,
            showWordFormationPbLayout,
            services,
            showFooter,
            zoomFactor,
        };

        return (
            <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={wrapperClassList.join(' ')}
                onMouseDown={annotatableQuestionHandler}
                onTouchStart={annotatableQuestionHandler}
            >
                { question.rubricBlockModel &&
                    <QuestionRubricBlockDisplay
                        model={question.rubricBlockModel}
                        onReady={this.rubricBlockTracker}
                    />
                }

                { shouldShowPdfPanel &&
                    <QuestionPDFPanelDisplay
                        model={question.PDFPanelModel}
                        onReady={this.pdfPanelTracker}
                        presignedUrlsForPDFPanel={presignedUrlsForPDFPanel}
                        questionContentRevisionId={question.contentRevisionId}
                        stimulusPosition={stimulusPosition}
                    />
                }

                {isA11yV5QuestionClickHandlerEnabled
                    ? this.renderMainQuestionLandmarkV5(renderParams)
                    : this.renderMainQuestionLandmark(renderParams)}

                {showKeyWordListBlock && !showWordFormationPbLayout &&
                    <QuestionKeyWordListDisplay {...this.props} question={question} />
                }

            </div>
        );
    }
}

export class QuestionDisplayPreparing extends React.PureComponent {
    static propTypes = {
        isReady: PropTypes.bool.isRequired,
        question: PropTypes.object,
    };

    componentDidMount() {
        const { isReady, question } = this.props;

        if (question && !isReady) {
            assessmentActions.prepareQuestion(question);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextQuestion = nextProps.question;

        if (nextQuestion && !nextProps.isReady) {
            assessmentActions.prepareQuestion(nextQuestion);
        }
    }

    render() {
        return this.props.isReady ? <Question {...this.props} /> : null;
    }
}

export class QuestionDisplayWrapper extends React.PureComponent {
    static propTypes = {
        isAllView: PropTypes.bool,
        isListeningComponent: PropTypes.bool,
        isPrintBased: PropTypes.bool,
        question: PropTypes.object,
        section: PropTypes.object,
        ui: PropTypes.object,
    };

    renderSectionRubric() {
        const { section, ui, isListeningComponent } = this.props;

        if (isListeningComponent && !uiService.isPaperBasedRenderForCEQ()) {
            return <SectionRubricBlock section={section} ui={ui} isPaperBased />;
        }
        return null;
    }


    render() {
        const { isAllView, isPrintBased, question } = this.props;
        const questionNumberingInSection = parseInt(question?.questionNumberingInSection, 10);
        return (
            <React.Fragment>
                { isPrintBased && isAllView && questionNumberingInSection === 1 &&
                    this.renderSectionRubric()
                }
                <QuestionDisplayPreparing {...this.props} />
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let question;

    if (ownProps && ownProps.question) {
        question = ownProps.question;
    } else {
        const contentRevisionId = ownProps.contentRevisionId || state.currentlyShown.questionId;
        question = assessmentReader.getQuestion(contentRevisionId, state);
    }

    if (!question) {
        return {
            isReady: false,
        };
    }

    return questionDisplaySelector.computeProps(state, ownProps, question);
};

const QuestionDisplayContainer = connect(mapStateToProps)(QuestionDisplayWrapper);
export default withIcewormFocusTrap(QuestionDisplayContainer);
